import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Bell, Calendar, MapPin, ChevronRight, X } from "lucide-react";

const Notifications = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    const saved = localStorage.getItem("dismissedNotifications");
    return saved ? JSON.parse(saved) : [];
  });

  const fetchNotifications = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/trips/upcoming/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Refresh every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  const dismissNotification = (tripId) => {
    const newDismissed = [...dismissed, tripId];
    setDismissed(newDismissed);
    localStorage.setItem("dismissedNotifications", JSON.stringify(newDismissed));
  };

  const activeNotifications = notifications.filter(n => !dismissed.includes(n.trip_id));
  const hasNotifications = activeNotifications.length > 0;

  const getUrgencyColor = (days) => {
    if (days === 0) return "text-destructive";
    if (days <= 2) return "text-accent";
    return "text-primary";
  };

  const getUrgencyBg = (days) => {
    if (days === 0) return "bg-destructive/10";
    if (days <= 2) return "bg-accent/10";
    return "bg-primary/10";
  };

  const getDaysText = (days) => {
    if (days === 0) return "Oggi!";
    if (days === 1) return "Domani";
    return `Tra ${days} giorni`;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notifications-btn"
        >
          <Bell className="w-5 h-5" />
          {hasNotifications && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center font-mono">
              {activeNotifications.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-semibold">Viaggi in arrivo</h3>
          <p className="text-xs text-muted-foreground">Prossimi 7 giorni</p>
        </div>
        
        {activeNotifications.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nessun viaggio imminente</p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {activeNotifications.map((trip) => (
              <div 
                key={trip.trip_id}
                className="p-3 border-b border-border last:border-0 hover:bg-secondary/30 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div 
                    className="flex-1 cursor-pointer"
                    onClick={() => {
                      navigate(`/trip/${trip.trip_id}`);
                      setIsOpen(false);
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${getUrgencyBg(trip.days_until)} ${getUrgencyColor(trip.days_until)}`}>
                        {getDaysText(trip.days_until)}
                      </span>
                    </div>
                    <h4 className="font-medium text-sm">{trip.title}</h4>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {trip.destination}
                      </span>
                      <span className="flex items-center gap-1 font-mono">
                        <Calendar className="w-3 h-3" />
                        {trip.start_date}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6 shrink-0"
                    onClick={() => dismissNotification(trip.trip_id)}
                    data-testid={`dismiss-notification-${trip.trip_id}`}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default Notifications;
